/**
 * AutoCreateDailyReportsModal — Vorschau + Massenerstellung von Bautagesberichten.
 *
 * Zeigt für jeden Tag im Projektzeitraum:
 *   - Datum + Wochentag
 *   - Status (wird erstellt / existiert bereits / Wochenende / Feiertag / keine Planung)
 *   - Mitarbeiter & Arbeitszeit aus Einsatzplanung
 *
 * Erlaubt Filter-Toggles (Wochenende / Feiertage / Tage ohne Planung) und ruft
 * dann den `/daily-reports-auto/create` Endpoint auf.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { authFetch } from "../App";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { Loader2, CalendarCheck, X, Sparkles, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  will_create: { label: "Wird erstellt",     cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  exists:      { label: "Existiert bereits", cls: "bg-gray-100 text-gray-600 border-gray-300" },
  weekend:     { label: "Wochenende",        cls: "bg-slate-50 text-slate-700 border-slate-200" },
  holiday:     { label: "Feiertag",          cls: "bg-amber-50 text-amber-700 border-amber-200" },
  no_planning: { label: "Keine Planung",     cls: "bg-blue-50 text-blue-700 border-blue-200" },
};

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || { label: status, cls: "bg-gray-50 text-gray-600 border-gray-200" };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold border ${s.cls}`}>
      {s.label}
    </span>
  );
}

export default function AutoCreateDailyReportsModal({ open, onClose, projectId, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [includeWeekends, setIncludeWeekends] = useState(true);
  const [includeHolidays, setIncludeHolidays] = useState(true);
  const [includeNoPlanning, setIncludeNoPlanning] = useState(true);

  const fetchPreview = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(`${API}/projects/${projectId}/daily-reports-auto/preview`);
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Vorschau fehlgeschlagen");
      }
      setData(await r.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (open && projectId) {
      fetchPreview();
    }
  }, [open, projectId, fetchPreview]);

  const willCreateCount = useMemo(() => {
    if (!data) return 0;
    return data.plan.filter((p) => {
      if (p.status === "exists") return false;
      if (p.status === "weekend" && !includeWeekends) return false;
      if (p.status === "holiday" && !includeHolidays) return false;
      if (p.status === "no_planning" && !includeNoPlanning) return false;
      return true;
    }).length;
  }, [data, includeWeekends, includeHolidays, includeNoPlanning]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const r = await authFetch(`${API}/projects/${projectId}/daily-reports-auto/create`, {
        method: "POST",
        body: JSON.stringify({
          include_weekends: includeWeekends,
          include_holidays: includeHolidays,
          include_no_planning: includeNoPlanning,
        }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || "Erstellung fehlgeschlagen");
      }
      const result = await r.json();
      const s = result.summary || {};
      toast.success(
        `${s.created_count || 0} Bautagesberichte erstellt. ` +
        `${s.existed_count || 0} bereits vorhanden, ${s.filtered_count || 0} ausgefiltert.` +
        (s.error_count ? ` ${s.error_count} Fehler.` : "")
      );
      if (onCreated) onCreated(result);
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !creating && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="auto-create-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-600" />
            Bautagesberichte automatisch erstellen
          </DialogTitle>
          <DialogDescription>
            Erzeugt für jeden Tag im Projektzeitraum einen Bautagesbericht im Status „Entwurf".
            Bestehende Berichte werden nicht überschrieben.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            Vorschau wird geladen...
          </div>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 p-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Vorschau fehlgeschlagen</div>
              <div className="text-sm">{error}</div>
            </div>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {/* Counters + Filter */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
              <StatCard label="Wird erstellt" value={willCreateCount} tone="emerald" testId="ac-stat-will-create" />
              <StatCard label="Existiert" value={data.counters.exists} tone="gray" testId="ac-stat-exists" />
              <StatCard label="Wochenende" value={data.counters.weekend} tone="slate" testId="ac-stat-weekend" />
              <StatCard label="Feiertag" value={data.counters.holiday} tone="amber" testId="ac-stat-holiday" />
              <StatCard label="Ohne Planung" value={data.counters.no_planning} tone="blue" testId="ac-stat-no-planning" />
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 flex flex-wrap gap-4 mb-3 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={includeWeekends} onChange={(e) => setIncludeWeekends(e.target.checked)} data-testid="ac-toggle-weekends" />
                Wochenenden einbeziehen
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={includeHolidays} onChange={(e) => setIncludeHolidays(e.target.checked)} data-testid="ac-toggle-holidays" />
                Feiertage einbeziehen
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={includeNoPlanning} onChange={(e) => setIncludeNoPlanning(e.target.checked)} data-testid="ac-toggle-no-planning" />
                Tage ohne Einsatzplanung
              </label>
              {data.bundesland && (
                <span className="ml-auto text-xs text-gray-500">
                  Feiertage für Bundesland: <span className="font-bold">{data.bundesland}</span>
                </span>
              )}
            </div>

            {/* Plan-Tabelle */}
            <div className="overflow-auto flex-1 border border-gray-200 rounded-lg bg-white" data-testid="ac-plan-table">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-semibold text-gray-700">Datum</th>
                    <th className="px-3 py-2 font-semibold text-gray-700">Tag</th>
                    <th className="px-3 py-2 font-semibold text-gray-700">Status</th>
                    <th className="px-3 py-2 font-semibold text-gray-700">Mitarbeiter</th>
                    <th className="px-3 py-2 font-semibold text-gray-700 text-right">Stunden</th>
                    <th className="px-3 py-2 font-semibold text-gray-700">Bericht</th>
                  </tr>
                </thead>
                <tbody>
                  {data.plan.map((p) => (
                    <tr key={p.date} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-1.5 font-mono text-xs">{formatDateDE(p.date)}</td>
                      <td className="px-3 py-1.5 text-gray-600">{p.weekday}</td>
                      <td className="px-3 py-1.5">
                        <StatusBadge status={p.status} />
                        {p.holiday_name && (
                          <span className="ml-1.5 text-[11px] text-amber-700">{p.holiday_name}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-gray-700">{p.personnel_count}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">
                        {p.total_hours > 0 ? `${p.total_hours.toFixed(2).replace(".", ",")} h` : "–"}
                      </td>
                      <td className="px-3 py-1.5 text-gray-500">
                        {p.existing_report_number || (p.status === "exists" ? "vorhanden" : "–")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <DialogFooter className="border-t pt-3">
          <Button variant="outline" onClick={onClose} disabled={creating} data-testid="ac-cancel-btn">
            <X className="w-4 h-4 mr-2" />Abbrechen
          </Button>
          <Button
            onClick={handleCreate}
            disabled={creating || loading || !data || willCreateCount === 0}
            data-testid="ac-create-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CalendarCheck className="w-4 h-4 mr-2" />}
            {willCreateCount > 0
              ? `${willCreateCount} fehlende Berichte erstellen`
              : "Keine Berichte zu erstellen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, tone, testId }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    gray: "border-gray-300 bg-gray-50 text-gray-700",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    blue: "border-blue-200 bg-blue-50 text-blue-700",
  };
  return (
    <div className={`rounded-lg border p-2 text-center ${tones[tone] || tones.gray}`} data-testid={testId}>
      <div className="text-lg font-extrabold tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-70">{label}</div>
    </div>
  );
}

function formatDateDE(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}
